import React, {useState, useEffect} from 'react'
import { Typography, Box, Stack, Button, Snackbar, Alert, ButtonGroup, IconButton, Tooltip } from '@mui/material';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { styled, useTheme } from '@mui/system';
import { useNavigate } from "react-router-dom";
import { useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form'
import { userURL, apiURL, storageURL } from './App';
import { authFetch, authJson, authBlob } from './apiClient';
import { SecondButton, GrayButton, RoundButton } from './Controls';
import { StandardTextField, StandardSelectFile } from './Controls';
import { StandardDatePicker, StandardTimePicker } from './Controls';
import { SingleFilledSelect } from './Controls';
import { GrayColorButton, SecondColorButton } from './Controls';
import { CheckFilledSelect } from './CheckSelect';
import PlaceSelectMap from './PlaceSelectMap';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import ChangeHistoryOutlinedIcon from '@mui/icons-material/ChangeHistoryOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import PlaceIcon from '@mui/icons-material/Place';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/CancelOutlined';

// 初期表示中心位置
const defaultCenter = {
  lat: parseFloat(process.env.REACT_APP_INIT_LAT || '36.000'),
  lng: parseFloat(process.env.REACT_APP_INIT_LON || '138.000')
};

// コンテナ
const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  padding: '30px',
  backgroundColor: '#fff',
  position: 'absolute',
  top: '70px',
  bottom: '0',
  width: '100vw',
  height: 'calc(100vh - 70px)',
  boxSizing: 'border-box',
  //overflow: 'hidden', // スクロールバーを出さない
});

// ファイルURLから File オブジェクト取得（失敗時は null）
async function getFile(url) {
  if (!url) return null;
  // URL 正規化（先頭スラッシュ除去、バックスラッシュをスラッシュに変換）
  const normalized = url.replace(/^\/+/, '').replace(/\\/g, '/');
  // パス分解
  const parts = normalized.split('/');
  // ファイル名部分を取得
  const last = parts.pop() || 'file';
  // ファイル名デコード
  let fileName;
  try { fileName = decodeURIComponent(last); } catch { fileName = last; }
  // ファイル名のみ再度エンコードし、URL生成（他セグメントは ASCII 前提）
  const encodedFileName = encodeURIComponent(fileName);
  const fullUrl = `${storageURL}/${parts.join('/')}/${encodedFileName}`;
console.log(storageURL)
console.log(fullUrl)
  try {
    // API呼び出し
    const blob = await authBlob(fullUrl);
    // BlobからFileオブジェクトを生成
    return new File([blob], fileName, { type: blob.type || 'application/octet-stream' });
  }
  catch {
    // 取得失敗
    return null;
  }
}

// 削除ボタン
const DeleteButton = (props) => {
    const [open, setOpen] = useState(false); // 確認ダイアログの表示/非表示

    // 確認ダイアログ表示
    const handleOpen = () => {
      setOpen(true);
    };
  
    // 確認ダイアログ非表示
    const handleClose = () => {
      setOpen(false);
    };

    // 削除ボタン
    const handleDelete = (e) => {
      // 確認ダイアログ非表示
      setOpen(false)
      // 親の削除イベント
      props.onClick(e)
    };

    // ボタンレンダリング    
    return (
      <>
        <Stack direction="row" sx={{width: '100%', height: '100%', justifyContent: "center", alignItems: "center"}} spacing={2}>
          <RoundButton onClick={handleOpen} variant="contained" color="error" sx={{width:"200px"}}>{props.children}</RoundButton>
        </Stack>
        <Dialog open={open} onClose={handleClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
          <DialogTitle id="alert-dialog-title">
            <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>取材の削除</Typography>
          </DialogTitle>
          <DialogContent sx={{pr:20}}>
            <DialogContentText id="alert-dialog-description">
              {/* DialogContentText は <p> を出力するためネストした <p> を避ける */}
              <Typography variant="inherit" component="span">この取材を削除しますか？</Typography>
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{p:2}}>
            <Button onClick={handleClose} variant="contained" style={{color: 'white', backgroundColor: 'gray'}} autoFocus>キャンセル</Button>
            <Button onClick={handleDelete} variant="contained" color="error">削除</Button>
          </DialogActions>
        </Dialog>        
      </>
    );
};

// 詳細ページ
const DetailsPage = (props) => {
  const theme = useTheme();
  const { control, register, handleSubmit, setValue, getValues, watch } = useForm();
  const navigate = useNavigate();
  const routerLocation = useLocation();

  const [ programs, setPrograms ] = useState([]);
  const [ users, setUsers ] = useState([]);
  const [ areas, setAreas ] = useState([]);
  const [ genres, setGenres ] = useState([]);
  const [ appointments, setAppointments ] = useState([]);
  const [ details, setDetails ] = useState({id:props.id});
  const [ files, setFiles ] = useState([]);
  const [ fileError, setFileError ] = useState('');
  const [ fileErrorOpen, setFileErrorOpen ] = useState(false);
  const [ location, setLocation ] = useState(null);
  const [ mark, setMark ] = useState(null);
  const [ mapOpen, setMapOpen ] = useState(false);
  const [ mapDraftLocation, setMapDraftLocation ] = useState(null); // ダイアログ内の一時位置

  // 取材名・取材場所のリアルタイム監視
  const watchInterviewName = watch('interview_name');
  const watchInterviewPlace = watch('interview_place');
  const hasInterviewName = (watchInterviewName && String(watchInterviewName).trim() !== '');

  // 放送予定のローカル状態（フォーム値から派生）
  const [isOnAir, setIsOnAir] = useState(false);

  // 初回マウント時と props.id 変更時
  useEffect(() => {
    // 並列取得
    void Promise.all([
      // ユーザリスト取得
      fetchUserList(),
      // 番組リスト取得
      fetchProgramList(),
      // エリアリスト取得
      fetchAreaList(),
      // ジャンルリスト取得
      fetchGenreList(),
      // アポイントメントリスト取得
      fetchAppointmentList(),
      // 取材データ取得
      fetchDetails()
    ]);
    // props.id を依存にすると別ID遷移で再取得
  }, [props.id])

  // 取材フラグをフォームに登録
  useEffect(() => {
    // react-hook-form へ登録（未登録だと submit に含まれないため）
    if (typeof register === 'function') {
      register('interview_mark');
    }
  }, [register]);

  // ユーザリスト取得
  const fetchUserList = async () => {
    try {
      // API呼び出し
      const data = await authJson(`${userURL}/fullnames/`);
      // ユーザリスト設定
      setUsers(data);
    }
    catch (e) {
      // 取得エラー
      console.log(e);
      // ユーザリスト初期化
      setUsers([]);
    }
  }

  // 番組リスト取得
  const fetchProgramList = async () => {
    try {
      // API呼び出し
      const data = await authJson(`${apiURL}/master/programs`);
      // 番組リスト設定
      setPrograms(data);
    }
    catch (e) {
      // 取得エラー
      console.log(e);
      // 番組リスト初期化
      setPrograms([]);
    }
  }

  // エリアリスト取得
  const fetchAreaList = async () => {
    try {
    // API呼び出し
      const data = await authJson(`${apiURL}/master/areas`);
      // エリアリスト設定
      setAreas(data);
    }
    catch (e) {
      // 取得エラー
      console.log(e);
      // エリアリスト初期化
      setAreas([]);
    }
  }

  // ジャンルリスト取得
  const fetchGenreList = async () => {
    try {
      // API呼び出し
      const data = await authJson(`${apiURL}/master/genres`);
      // ジャンルリスト設定
      setGenres(data);
    }
    catch (e) {
      // 取得エラー
      console.log(e);
      // ジャンルリスト初期化
      setGenres([]);
    }
  }

  // アポイントメントリスト取得
  const fetchAppointmentList = async () => {
    try {
      // API呼び出し
      //const data = await authJson(`${apiURL}/master/appointments`);
      const res = await fetch("/appointments.json");
      const data = await res.json();
      if (!Array.isArray(data)) {
        throw new Error('appointments.json unexpected format: ' + JSON.stringify(data));
      }
      // アポイントメントリスト設定
      setAppointments(data);
    }
    catch (e) {
      // 取得エラー
      console.log(e);
      // アポイントメントリスト初期化
      setAppointments([]);
    }
  }

  // 取材データ取得
  const fetchDetails = async () => {
    // 取材データとファイルリスト初期化
    let newDetails = {id:props.id}
    let newFiles = []
    // 取材データ取得開始
    try {
      // API呼び出し
      const data = await authJson(`${apiURL}/interviews/${props.id}`);
      // 取材データがある場合
      if (data != null) {
        // 取材データ設定
        newDetails = data      
        // 取材データ項目数分
        for (let name in data) {
          // ファイルリスト以外をフォーム値へ設定
          if (name != "files") {
            setValue(name, data[name]);
          }
        }
        // 取材フラグのローカル状態反映
        if (data.mark !== undefined) {
          setMark(data.mark);
        }
        // 放送予定のローカル状態反映
        if (data.is_onair !== undefined) {
          setIsOnAir(data.is_onair);
        }
        // ファイルリスト作成
        if (data.files != null) {
          // すべてのファイルを取得
          const fetched = await Promise.all(
            data.files.map(src => getFile(src).catch(() => null))
          );
          // null でないものだけ抽出
          newFiles = fetched.filter(f => !!f);
        }
        // 緯度経度が設定されている場合
        if (data.lon != null && data.lat != null) {
          // 位置情報設定
          setLocation({name: data.interview_place, lat: data.lat, lon: data.lon})
        }
      }
    }
    catch (e) {
      // 取得エラー
      console.log(e);
    }
    // 取材データ設定
    setDetails(newDetails)
    // ファイルリスト設定
    setFiles(newFiles)
  }

  // キャンセル
  const onCancelClick = () => {
    // 新規入力の場合
    if (props.mode == "New") {
      // メニューに戻る
      navigate("/");
    }
    else {
      // 1つ前に戻る
      navigate(-1);
    }
  };

  // 削除ボタン
  const onDeleteClick = () => {
    // リクエストデータ作成
    const data = {id: props.id}
    // ユーザ情報が存在する場合
    if (props.user != null) {
      data.change_account = props.user.username;
    }
    // フォームデータ追加
    const body = new FormData()
    body.append("props", JSON.stringify(data))
    // 取材データ削除API呼び出し
    authFetch(`${apiURL}/interviews/delete`, { method: 'POST', body, actionName: 'RemoveInterview' })
      .then(() => {
        // 呼び出し元がある場合
        if (routerLocation.state && routerLocation.state.referrer) {
          // 呼び出し元へ遷移
          navigate(routerLocation.state.referrer, { replace: true });
        } else {
          // それ以外はトップ画面へ
          navigate('/', { replace: true });
        }
      })
      .catch((e) => {
        // 削除エラー
        console.log(e);
      });
  };

  // 取材データ更新
  const updateData = async (data) =>{
    // 放送予定がなしの場合は放送日をクリア
    if (data.is_onair === false) {
      data.program_date = null;
    }
    // 位置情報が存在する場合
    if (location != null) {
      data.lat = location.lat;
      data.lon = location.lon;
    } else {
      data.lat = null;
      data.lon = null;
    }
    // ユーザ情報が存在する場合
    if (props.user != null) {
      data.change_account = props.user.username;
    }
    // ファイル操作ありを通知
    data.is_files = true
    // フォームデータ追加
    const body = new FormData()
    body.append("props", JSON.stringify(data))
    for (let file of files) {
      body.append("files", file)
    }
    // 取材データ更新API呼び出し
    authFetch(`${apiURL}/interviews/update`, { method: 'POST', body, actionName: 'EditInterview' })
      .then(async () => {
        // 放送日が変わった場合
        if (data.program_date != details.program_date) {
          // 放送予定リスト更新
          await broadcastRowUpdate(data);
        }
        // 呼び出し元がある場合
        if (routerLocation.state && routerLocation.state.referrer) {
          // 呼び出し元へ遷移
          navigate(routerLocation.state.referrer, { replace: true });
        } else {
          // それ以外はトップ画面へ
          navigate('/', { replace: true });
        }
      })
      .catch((e) => {
        // 更新エラー
        console.log(e);
      });
  }

  // 放送予定リスト更新
  const broadcastRowUpdate = async (row) => {
    console.log('放送予定更新開始', row);
    // DB検索API呼び出し用
    let existBroadcast = null;
    // API呼び出し用
    const body = new FormData();
    const form_params = { };
    form_params.program_date = row.program_date;
    form_params.interview_id = row.id;
    form_params.clip_id = null;
    form_params.onair_order = null;
    form_params.entry_account = props.user.username;
    form_params.change_account = props.user.username;
    // 更新処理
    try {
      // 放送予定が存在するか確認のためAPI呼び出し
      const query_params = new URLSearchParams({ interview_id: row.id });
      const data = await authJson(`${apiURL}/broadcasts?` + query_params);
      console.log('放送予定検索結果', data);
      if (data && Array.isArray(data) && data.length > 0) {
        existBroadcast = data[0];
      }
      // 放送予定が存在する場合
      if (existBroadcast) {
        // idを追加する
        form_params.id = existBroadcast.id;
        // 放送予定　更新API呼び出し
        body.append('props', JSON.stringify(form_params));
        await authFetch(`${apiURL}/broadcasts/update`, { method: 'POST', body, actionName: 'AddInterviewToBroadcast' });
      }
      // 放送予定が存在しない場合
      else {
        // 放送予定　新規登録API呼び出し
        body.append('props', JSON.stringify(form_params));
        await authFetch(`${apiURL}/broadcasts/create`, { method: 'POST', body, actionName: 'AddInterviewToBroadcast' });
      }
    } catch(e){ console.log(e); }
  };
  
  // 地図で位置が選択されたときの処理
  const handleMapPlaceSelected = (place) => {
    console.log('検索で選択:', place)

    // 位置設定がない場合
    if(!place){
      // 位置クリア
      setMapDraftLocation(null)
      return
    }
    // 位置設定
    place.event = "suggestion"   // 検索で選択されたことを示すフラグを追加
    setMapDraftLocation(place)
  }

  // 地図位置変更(クリックでマーカー変更)
  function handleMapMarkerChange(marker){
    console.log('クリックでマーカー変更:', marker)

    // 位置設定がない場合
    if(!marker){
      // 位置クリア
      setMapDraftLocation(null)
      return
    }
    // 位置設定
    marker.event = "click"    // クリックで変更されたことを示すフラグを追加
    setMapDraftLocation(marker)
  }

  // 地図ダイアログ表示
  const handleMapDlgOpen = () => {
    // ダイアログ用の位置を設定（現在の位置 or null）
    setMapDraftLocation(location ? {lat:location.lat, lng:location.lon, name:location.name} : null);     
    // ダイアログ表示
    setMapOpen(true);
  }

  // 地図ダイアログでOKが押されたときの処理
  const handleMapDlgOK = async () => {
    // ダイアログを閉じる
    setMapOpen(false);

    // 位置がない場合
    if(!mapDraftLocation) {
      // 位置削除
      setLocation(null);
      setValue('interview_place', '', { shouldDirty: true });
      return;
    }

    // 検索バーから選択された位置の場合
    if (mapDraftLocation.event === "suggestion") {
      // 位置設定
      setLocation({lat:mapDraftLocation.lat, lon:mapDraftLocation.lng, name:mapDraftLocation.name});
      setValue('interview_place', mapDraftLocation.name || '', { shouldDirty: true });
      return;
    }

    // Google Maps API ロードチェック
    if (!window.google?.maps?.places) {
      console.error("Google Maps API not loaded");
      return;
    } 
    // Google Maps API の Geocoder と PlacesService を初期化
    const geocoder = new window.google.maps.Geocoder();
    const service = new window.google.maps.places.PlacesService(document.createElement("div"));    
    const latLng = new window.google.maps.LatLng(mapDraftLocation.lat, mapDraftLocation.lng);

    // 1. PlaceId がある場合は getDetails
    const getPlaceNameFromPlaceId = () => {
      return new Promise((resolve) => {
        if (!mapDraftLocation.placeId) return resolve(null);
        service.getDetails(
          { placeId: mapDraftLocation.placeId, fields: ['geometry', 'name', 'formatted_address'] },
          (place, status) => {
            if (status === "OK" && place) {
              console.log("getPlaceNameFromPlaceId", place)
              resolve({ lat: place.geometry.location.lat(), lng: place.geometry.location.lng(), name: place.name });
            } else {
              console.warn("getPlaceNameFromPlaceId failed:", status);
              resolve(null);
            }
          }
        );
      });
    };

    // 2. NearbySearch
    const getPlaceNameFromNearby = () => {
      return new Promise((resolve) => {
        service.nearbySearch({ location: latLng, radius: 30 }, (res, status) => {
          if (status === "OK" && res.length > 0) {
            console.log("getPlaceNameFromNearby", res[0])
            resolve({ lat: mapDraftLocation.lat, lng: mapDraftLocation.lng, name: res[0].name });
          } else {
            console.warn("getPlaceNameFromNearby failed:", status);
            resolve(null);
          }
        });
      });
    };

    // 3. 逆ジオコーディング
    const getPlaceNameFromGeocode = () => {
      // 名称取得
      const getName = (result) => {
        // formatted_addressじゃ日本か郵便番号まで入ってくるので除去
        let addr = result.formatted_address.replace(/^日本、?|〒\d{3}-\d{4}\s*/g, '');
        return addr;
      };
      // 逆ジオコーディング実行
      return new Promise((resolve) => {
        geocoder.geocode({ location: latLng }, (res, status) => {
          if (status === "OK" && res.length > 0) {
            console.log("getPlaceNameFromGeocode", res[0])
            resolve({ lat: mapDraftLocation.lat, lng: mapDraftLocation.lng, name: getName(res[0]) });
          } else {
            console.warn("getPlaceNameFromGeocode failed:", status);
            resolve({ lat: mapDraftLocation.lat, lng: mapDraftLocation.lng, name: null });
          }
        });
      });
    };

    // 位置名称の取得を実行
    let place = await getPlaceNameFromPlaceId();
    //if (!place) place = await getPlaceNameFromNearby();  // 名称かなりの確率で取得できるが課金対象APIなので注意
    if (!place) place = await getPlaceNameFromGeocode();
    console.log("取得した位置情報:", place)

    // 位置設定
    setLocation({lat:place.lat, lon:place.lng, name:place.name});
    setValue('interview_place', place.name || '', { shouldDirty: true });
  }

  // レンダリング
  return (
    <>
    <Container>
      {/* 3カラムヘッダ: (1)タイトル+説明 (2) 取材名/場所 (3) mark選択 */}
      <Box sx={{ width:'100%', mb:1 }}>
        <Stack direction="row" alignItems="stretch" spacing={2} sx={{ width:'100%' }}>
          {/* 1列目: タイトル + 説明 (左寄せ) / 全体 25% */}
          <Box sx={{ flex:1, minWidth:0, textAlign:'left', display:'flex', flexDirection:'column', justifyContent:'flex-start', py:0 }}>
            <Typography variant="h5" sx={{ fontWeight:'bold' }}>詳細入力</Typography>
            <Typography variant="inherit" color="text.disabled">詳細を入力してください。</Typography>
          </Box>
          {/* 2列目: 取材名 / 取材場所 (中央寄せ, 行間圧縮) / 全体 50% */}
          <Box sx={{ flex:2, minWidth:0, display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', textAlign:'center', py:0 }}>
            {hasInterviewName && (
              <>
                <Typography
                  variant="h5"
                  title={watchInterviewName}
                  sx={{
                    fontWeight:'bold',
                    lineHeight:1.2,
                    mb: (watchInterviewPlace && String(watchInterviewPlace).trim() !== '') ? 0.25 : 0,
                    whiteSpace:'nowrap',
                    overflow:'hidden',
                    textOverflow:'ellipsis',
                    maxWidth:'100%',
                    width:'100%'
                  }}
                >{watchInterviewName}</Typography>
                {watchInterviewPlace && String(watchInterviewPlace).trim() !== '' && (
                  <Typography
                    variant="h6"
                    title={watchInterviewPlace}
                    sx={{
                      fontWeight:'bold',
                      lineHeight:1.2,
                      color:'text.secondary',
                      whiteSpace:'nowrap',
                      overflow:'hidden',
                      textOverflow:'ellipsis',
                      maxWidth:'100%',
                      width:'100%'
                    }}
                  >{watchInterviewPlace}</Typography>
                )}
              </>
            )}
          </Box>
          {/* 3列目: mark ボタン群 (右下寄せ, コンパクト化) / 全体 25% */}
          <Box sx={{ flex:1, minWidth:0, display:'flex', justifyContent:'flex-end', alignItems:'flex-end', py:0 }}>
            <ButtonGroup size="medium" aria-label="取材フラグ">
              <Button
                variant={mark === 1 ? 'contained' : 'outlined'}
                color="success"
                onClick={() => { setMark(1); setValue('mark', 1, { shouldDirty: true }); }}
                startIcon={<CircleOutlinedIcon fontSize="small" />}
              >取材</Button>
              <Button
                variant={mark === 2 ? 'contained' : 'outlined'}
                color="warning"
                onClick={() => { setMark(2); setValue('mark', 2, { shouldDirty: true }); }}
                startIcon={<ChangeHistoryOutlinedIcon fontSize="small" />}
              >保留</Button>
              <Button
                variant={mark === 3 ? 'contained' : 'outlined'}
                color="error"
                onClick={() => { setMark(3); setValue('mark', 3, { shouldDirty: true }); }}
                startIcon={<ClearOutlinedIcon fontSize="small" />}
              >不要</Button>
            </ButtonGroup>
          </Box>
        </Stack>
      </Box>
      {/* フォームをスクロール領域 + 固定アクションバーに分離 */}
      <Box sx={{ flex:1, width:'100%', display:'flex', flexDirection:'column', minHeight:0 }}>
        <Box sx={{ flex:1, overflowY:'auto', pr:0, minHeight:0 }}>
          <Box component="form" id="updateForm" onSubmit={handleSubmit(updateData)} sx={{ width:'100%', display:'flex', flexDirection:'column', pb:0 }}>
            {/* 3列入力エリア: 各列をflex:1で等幅 (高さ残余を利用) */}
            <Stack direction="row" spacing={3} sx={{ my:1, width:'100%', flexGrow:1, minHeight:0, alignItems:'stretch' }}>
              {/* 枠付き: 基本情報グループ */}
              <Box sx={{ width:'100%', flex:1, minWidth:0, p:2, border:'1px solid #cfd0d3', borderRadius:2 }}>
                <Stack direction="column" spacing={2} sx={{ width:'100%', flex:1, minWidth:0 }}>
                  <CheckFilledSelect id="program_ids" label="番組名" placeholder="番組名をプルダウンから選択" value={getValues("program_ids")} options={programs} register={register}/>
                  <StandardTextField id="interview_name" label="取材名" control={control} setValue={setValue} showClose/>
                  <CheckFilledSelect id="ucv_person_ids" label="取材担当者" placeholder="担当者名をプルダウンから選択" value={getValues("ucv_person_ids")} options={users} register={register}/>
                  <SingleFilledSelect id="is_onair" label="放送予定" placeholder="プルダウンから選択" value={getValues("is_onair")} options={[ {id: true, name: 'あり'}, {id: false, name: 'なし'}]} onChange={setIsOnAir} register={register}/>
                  <StandardTimePicker id="program_length" label="放送尺" control={control} showSeconds={true} showClose/>
                  <StandardDatePicker id="program_date" label="放送日" control={control} disabled={!isOnAir} showClose/>
                  <CheckFilledSelect id="genre_ids" label="ジャンル" placeholder="ジャンルをプルダウンから選択" value={getValues("genre_ids")} options={genres} register={register}/>
                  <StandardSelectFile
                    id="detail-files"
                    label="資料"
                    placeholder="資料をアップロード"
                    files={files}
                    setFiles={(f) => { setFiles(f); }}
                    accept="image/*,application/pdf,text/*,.txt,.csv,.json,.md,.log"
                    maxFiles={20}
                    maxSizeMB={50}
                    onError={(msg) => { setFileError(msg); setFileErrorOpen(true); }}
                  />
                </Stack>
              </Box>
              <Stack direction="column" spacing={3} sx={{ width:'100%', flex:1, minWidth:0 }}>
                {/* 枠付き: 日付・時間グループ */}
                <Box sx={{ p:2, border:'1px solid #cfd0d3', borderRadius:2 }}>
                  <Stack direction="column" spacing={2}>
                    <StandardDatePicker id="interview_date" label="取材日" control={control} showClose/>
                    <StandardTimePicker id="interview_starttime" label="取材開始時間" control={control} showClose />
                    <StandardTimePicker id="interview_endtime" label="取材終了時間" control={control} showClose />
                  </Stack>
                </Box>
                {/* 枠付き: 場所/エリア/担当者グループ */}
                <Box sx={{ p:2, border:'1px solid #cfd0d3', borderRadius:2 }}>
                  <Stack direction="column" spacing={2}>
                    <StandardTextField
                      id="interview_place"
                      label="取材場所"
                      control={control}
                      setValue={setValue}
                      endAdornment={
                        <>
                          <Tooltip title="地図を表示 / 位置を設定">
                            <IconButton size="small" onClick={handleMapDlgOpen}>
                              <PlaceIcon />
                            </IconButton>
                          </Tooltip>
                          <IconButton size="small" onClick={() => setValue('interview_place', '', { shouldDirty: true })}>
                            <CancelIcon/>
                          </IconButton>
                        </>
                      }
                    />
                    <CheckFilledSelect id="area_ids" label="エリア" placeholder="エリアをプルダウンから選択" value={getValues("area_ids")} options={areas} register={register}/>
                    <SingleFilledSelect id="interview_appointment" label="アポイントメント状況" placeholder="アポイントメント状況をプルダウンから選択" value={getValues("interview_appointment")} options={appointments} register={register}/>
                    <StandardTextField id="interview_person" label="取材先担当者" control={control} setValue={setValue} showClose/>
                    <StandardTextField id="interview_contact" label="取材先連絡先" control={control} setValue={setValue} showClose/>
                  </Stack>
                </Box>
              </Stack>
              <Stack direction="column" spacing={2} sx={{ width:'100%', flex:1, minWidth:0 }}>
                {/* 枠付き: 内容 / メモ グループ */}
                <Box sx={{ p:2, border:'1px solid #cfd0d3', borderRadius:2 }}>
                  <Stack direction="column" spacing={2}>
                    <StandardTextField id="interview_contents" label="内容" multiline showClose rows={5} control={control} setValue={setValue}/>
                    <StandardTextField id="interview_memos" label="メモ" multiline showClose rows={5} control={control} setValue={setValue}/>
                  </Stack>
                </Box>
                {/* 枠付き: 登録者 */}
                <Box sx={{ p:2, border:'1px solid #cfd0d3', borderRadius:2, width: '200px' }}>
                  <Stack direction="column" spacing={2}>
                    <Typography sx={{ fontSize: 14 }}>登録者：{details.entry_account_name || '-'}</Typography>
                  </Stack>
                </Box>
                {/* 余白中央に削除ボタン配置 */}
                <Box sx={{ flexGrow:1, display:'flex', alignItems:'center', justifyContent:'center'}}>
                  <DeleteButton onClick={onDeleteClick}>この取材を削除する</DeleteButton>
                </Box>
              </Stack>
            </Stack>
          </Box>
        </Box>
        {/* 常時表示アクションバー */}
        <Box sx={{ width:'100%', display:'flex', justifyContent:'flex-end', m:0, py:0, backgroundColor:'#ffffff', position:'relative', zIndex:2 }}>
          <Box sx={{ display:'flex', gap:2 }}>
            <GrayButton type="button" variant="contained" onClick={onCancelClick} sx={{width:"200px"}}>キャンセル</GrayButton>
            <SecondButton type="submit" form='updateForm' variant="contained" sx={{width:"200px"}}>編集内容を保存</SecondButton>
          </Box>
        </Box>
      </Box>
    </Container>
    {/* 地図ダイアログ */}
    <Dialog open={mapOpen} onClose={() => setMapOpen(false)} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', pr:1 }}>
        <Box sx={{ flex:1, textAlign:'left', fontSize:16, fontWeight:'bold' }}>位置を選択</Box>
        <Box sx={{ flex:1, textAlign:'center' }}>
          <Button size="medium" color="error" onClick={() => setMapDraftLocation(null)} disabled={!mapDraftLocation}>
            選択中の位置を削除
          </Button>
        </Box>
        <Box sx={{ flex:1, display:'flex', justifyContent:'flex-end' }}>
          <IconButton size="small" onClick={() => setMapOpen(false)} aria-label="close"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p:0 }}>
        <Box sx={{ width:'100%', textAlign:'left', pl:2, pt:0, minHeight:0 }}>
          <Typography variant="caption" sx={{ color:'red', fontSize:'0.82rem', fontWeight:400, lineHeight:1 }}>
            ※地図検索で無料枠を超えた場合、課金が発生します
          </Typography>
        </Box>
        <Box sx={{ height: '60vh', position:'relative' }}>
          <PlaceSelectMap
            initialMarker={mapDraftLocation}
            defaultCenter={defaultCenter}
            onPlaceSelected={handleMapPlaceSelected}
            onMarkerChange={handleMapMarkerChange}
            countryRestriction="JP"
          />          
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent:'flex-end' }}>
        <GrayColorButton onClick={() => { setMapOpen(false); }}>キャンセル</GrayColorButton>
        <SecondColorButton onClick={handleMapDlgOK}>
          OK
        </SecondColorButton>
      </DialogActions>
    </Dialog>
    {/* ファイル関連エラートースト */}
    <Snackbar
      open={fileErrorOpen}
      autoHideDuration={4000}
      onClose={(e, reason) => { if (reason === 'clickaway') return; setFileErrorOpen(false); setFileError(''); }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Alert onClose={() => { setFileErrorOpen(false); setFileError(''); }} severity="warning" variant="filled" sx={{ width: '100%' }}>
        {fileError}
      </Alert>
    </Snackbar>
    </>
  );
};

export default DetailsPage;